import React from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { Invitation } from "@/services/invitations";
import TrialLocked from "@/components/trial/TrialLocked";
import { useTrialAccount } from "@/hooks/useTrialAccount";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import InlineEdit from "./InlineEdit";
import { validateCompany, validateEmail } from "./fieldRules";
import {
  CARD_BODY,
  CARD_CELL,
  CARD_HEADER,
  CARD_LINE_START,
  CARD_ROW,
  CARD_TABLE,
  TableMessageRow,
  TableSkeleton,
} from "./tableStates";

/**
 * Smallest md+ table width with and without the Company column: the fixed columns below plus
 * a 12rem floor for Email. Narrower cards scroll the table inside the card instead of
 * squeezing Email away.
 */
const MIN_TABLE_WIDTH = { plain: "md:min-w-[38rem]", company: "md:min-w-[49rem]" };

// Phone card slots for the action buttons (on the lock wrapper when locked, else the button).
const DELETE_ON_PHONE = "max-md:order-2 max-md:mr-3";
const RESEND_ON_PHONE = "max-md:order-6 max-md:mr-3";

const lastSent = (invitation: Invitation) =>
  invitation.last_invitation_sent_at ?? invitation.created_at;

interface InvitationsTableProps {
  invitations: Invitation[];
  loading: boolean;
  /** Load failure text; replaces the rows. */
  error: React.ReactNode;
  /** Broker invitations carry a company, editable in place. */
  showCompany: boolean;
  emptyMessage: React.ReactNode;
  busyInvitationId: number | null;
  onChangeEmail: (invitation: Invitation, email: string) => Promise<unknown>;
  onChangeCompany: (invitation: Invitation, companyName: string) => Promise<unknown>;
  onEditError: (message: string) => void;
  onResend: (invitation: Invitation) => void;
  /** Revokes it: it leaves the list and can't be accepted, but the record stays. */
  onDelete: (invitation: Invitation) => void;
}

/** Pending invitations; revoked, expired, and accepted ones are never listed. */
const InvitationsTable: React.FC<InvitationsTableProps> = ({
  invitations,
  loading,
  error,
  showCompany,
  emptyMessage,
  busyInvitationId,
  onChangeEmail,
  onChangeCompany,
  onEditError,
  onResend,
  onDelete,
}) => {
  // Names the focusable lock wrapper a trial account gets around each action.
  const { lockMessage } = useTrialAccount();
  const columnCount = showCompany ? 5 : 4;
  if (loading) return <TableSkeleton columns={columnCount} />;

  // A fixed layout from md up: set column widths, Email takes the rest, and every cell
  // truncates, so an open editor can't widen a column.
  return (
    <Table
      className={`${CARD_TABLE} md:table-fixed ${MIN_TABLE_WIDTH[showCompany ? "company" : "plain"]}`}
    >
      <TableHeader className={CARD_HEADER}>
        <TableRow>
          <TableHead>Email</TableHead>
          {showCompany && <TableHead className="w-44">Company</TableHead>}
          <TableHead className="w-36">Last sent</TableHead>
          <TableHead className="w-28">Expires</TableHead>
          <TableHead className="w-40 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className={CARD_BODY}>
        {error ? (
          <TableMessageRow columns={columnCount} error>
            {error}
          </TableMessageRow>
        ) : invitations.length === 0 ? (
          <TableMessageRow columns={columnCount}>{emptyMessage}</TableMessageRow>
        ) : (
          invitations.map((invitation) => (
            // Phone card lines: email | trash, company (brokers), sent by, expires | Resend.
            <TableRow key={invitation.id} className={CARD_ROW}>
              <TableCell className={cn(CARD_CELL, CARD_LINE_START, "font-medium max-md:order-1")}>
                <InlineEdit
                  value={invitation.email}
                  label={`email of the invitation to ${invitation.email}`}
                  field="email"
                  type="email"
                  normalize={(value) => value.trim().toLowerCase()}
                  validate={validateEmail}
                  onSave={(email) => onChangeEmail(invitation, email)}
                  onError={onEditError}
                  className="w-full"
                >
                  <span className="min-w-0 truncate" title={invitation.email}>
                    {invitation.email}
                  </span>
                </InlineEdit>
              </TableCell>
              {showCompany && (
                <TableCell className={cn(CARD_CELL, "max-md:order-3")}>
                  <InlineEdit
                    value={invitation.company_name}
                    label={`company of the invitation to ${invitation.email}`}
                    field="company_name"
                    validate={(value) => validateCompany(value, false)}
                    onSave={(companyName) => onChangeCompany(invitation, companyName)}
                    onError={onEditError}
                    className="w-full"
                  >
                    <span className="min-w-0 truncate" title={invitation.company_name}>
                      {invitation.company_name || "—"}
                    </span>
                  </InlineEdit>
                </TableCell>
              )}
              <TableCell
                className={cn(CARD_CELL, "text-muted-foreground max-md:order-4 md:text-foreground")}
              >
                <span className="block whitespace-nowrap">
                  <span className="md:hidden">Sent </span>
                  {formatDate(lastSent(invitation))}
                </span>
                {invitation.invited_by_email && (
                  <span
                    className="block truncate text-xs text-muted-foreground"
                    title={invitation.invited_by_email}
                  >
                    by {invitation.invited_by_email}
                  </span>
                )}
              </TableCell>
              <TableCell
                className={cn(
                  CARD_CELL,
                  CARD_LINE_START,
                  "whitespace-nowrap text-muted-foreground max-md:order-5 md:text-foreground"
                )}
              >
                <span className="md:hidden">Expires </span>
                {formatDate(invitation.expires_at)}
              </TableCell>
              {/* On a phone the actions join the card's lines: trash by the email, Resend last. */}
              <TableCell className={cn(CARD_CELL, "max-md:contents md:text-right")}>
                <div className="flex gap-2 max-md:contents md:justify-end">
                  <TrialLocked
                    lock="team"
                    inline
                    className={RESEND_ON_PHONE}
                    aria-label={`Resend invitation to ${invitation.email}: ${lockMessage("team")}`}
                  >
                    <Button
                      className={RESEND_ON_PHONE}
                      variant="outline"
                      size="sm"
                      disabled={busyInvitationId === invitation.id}
                      onClick={() => onResend(invitation)}
                      aria-label={`Resend invitation to ${invitation.email}`}
                    >
                      <RefreshCw className="mr-1 h-3.5 w-3.5" />
                      Resend
                    </Button>
                  </TrialLocked>
                  <TrialLocked
                    lock="team"
                    inline
                    className={DELETE_ON_PHONE}
                    aria-label={`Delete invitation to ${invitation.email}: ${lockMessage("team")}`}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "h-8 w-8 text-muted-foreground hover:text-destructive",
                        DELETE_ON_PHONE
                      )}
                      disabled={busyInvitationId === invitation.id}
                      onClick={() => onDelete(invitation)}
                      aria-label={`Delete invitation to ${invitation.email}`}
                      title={`Delete invitation to ${invitation.email}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TrialLocked>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

export default InvitationsTable;
